/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import {
  X, Sparkles, Send, Copy, Check, ChevronDown,
  Mail, Instagram, MessageSquare, Globe, MapPin,
  Users, Tag, ExternalLink, Trash2, CheckCircle,
  Clock, RefreshCw
} from "lucide-react";
import { useGrowth } from "../../../context/GrowthContext";
import { GrowthService, PIPELINE_STAGES } from "../../../services/growth/GrowthService";
import { ManagerAgent } from "../../../services/growth/agents/ManagerAgent";

const STAGE_COLORS = {
  new: "bg-gray-100 text-gray-700",
  researched: "bg-blue-50 text-blue-700",
  outreach_sent: "bg-yellow-50 text-yellow-700",
  replied: "bg-purple-50 text-purple-700",
  demo_scheduled: "bg-green-50 text-green-700",
  converted: "bg-emerald-50 text-emerald-700",
  not_interested: "bg-red-50 text-red-600",
};

const CHANNEL_ICONS = {
  email: Mail,
  instagram_dm: Instagram,
  text: MessageSquare,
  contact_form: Globe,
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      onClick={handle}
      className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition"
    >
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function LeadDetailPanel({ lead, onClose }) {
  const { moveStage, setAgentRunning, refreshAfterAgent, deleteLead, settings } = useGrowth();
  const [running, setRunning] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [activeChannel, setActiveChannel] = useState("email");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const currentStage = PIPELINE_STAGES.find((s) => s.id === lead.status);

  const handleRunPipeline = async () => {
    setRunning(true);
    setStatusMsg("Starting...");
    setAgentRunning("pipeline", true);
    try {
      const { research, outreach } = await ManagerAgent.runPipeline(lead, setStatusMsg, settings?.signupUrl, settings?.demoBookingUrl, settings?.voiceSamples);
      refreshAfterAgent(lead.id, { research, outreach, status: "researched" });
      setStatusMsg("Done.");
    } catch (err) {
      setStatusMsg(`Error: ${err.message}`);
    } finally {
      setRunning(false);
      setAgentRunning("pipeline", false);
    }
  };

  const handleDelete = async () => {
    await deleteLead(lead.id);
    onClose();
  };

  const handleMarkSent = async (channel) => {
    await GrowthService.markOutreachSent(lead.id, channel);
    refreshAfterAgent(lead.id, { status: "outreach_sent" });
  };

  const outreach = lead.outreach;
  const research = lead.research;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white h-full shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-gray-100">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900 truncate">{lead.name}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STAGE_COLORS[lead.status] || "bg-gray-100 text-gray-600"}`}>
                {currentStage?.label || lead.status}
              </span>
              {lead.score && (
                <span className="px-2 py-0.5 bg-gray-900 text-white rounded-full text-xs font-medium">
                  Score {lead.score}/10
                </span>
              )}
            </div>
            {lead.eventName && (
              <p className="text-sm text-gray-500 mt-0.5">{lead.eventName} · {lead.city}</p>
            )}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 ml-3">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Contact info */}
          <div className="grid grid-cols-2 gap-3">
            {lead.email && (
              <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">{lead.email}</span>
              </a>
            )}
            {lead.instagram && (
              <a href={`https://instagram.com/${lead.instagram.replace("@", "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                <Instagram className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">{lead.instagram}</span>
              </a>
            )}
            {lead.website && (
              <a href={lead.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
                <ExternalLink className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="truncate">Website</span>
              </a>
            )}
            {lead.city && (
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                {lead.city}
              </span>
            )}
            {lead.estimatedVendorCount && (
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <Users className="w-4 h-4 text-gray-400 shrink-0" />
                ~{lead.estimatedVendorCount} vendors
              </span>
            )}
            {lead.eventType && (
              <span className="flex items-center gap-2 text-sm text-gray-500">
                <Tag className="w-4 h-4 text-gray-400 shrink-0" />
                {lead.eventType}
              </span>
            )}
          </div>

          {/* Stage mover */}
          <div>
            <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Move Stage</p>
            <div className="flex flex-wrap gap-2">
              {PIPELINE_STAGES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => moveStage(lead.id, s.id)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                    lead.status === s.id
                      ? "bg-gray-900 text-white border-gray-900"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* AI Run Button */}
          {!research && (
            <button
              onClick={handleRunPipeline}
              disabled={running}
              className="w-full flex items-center justify-center gap-2 py-3 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition disabled:opacity-60"
            >
              <Sparkles className="w-4 h-4" />
              {running ? statusMsg || "Running agents..." : "Run AI — Research + Generate Outreach"}
            </button>
          )}

          {running && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              {statusMsg}
            </div>
          )}

          {/* Research */}
          {research && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Research</p>
                <button
                  onClick={handleRunPipeline}
                  disabled={running}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition"
                >
                  <RefreshCw className="w-3 h-3" />
                  Regenerate
                </button>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <p className="text-sm text-gray-700 leading-relaxed">{research.summary}</p>

                {research.painPoints?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Pain Points</p>
                    <ul className="space-y-1">
                      {research.painPoints.map((p, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="text-gray-400 mt-0.5">•</span> {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {research.opportunities?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">Pebble Opportunities</p>
                    <ul className="space-y-1">
                      {research.opportunities.map((o, i) => (
                        <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                          <span className="text-green-500 mt-0.5">✓</span> {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {research.fitReason && (
                  <p className="text-xs text-gray-500 italic border-t border-gray-200 pt-2">
                    {research.fitReason}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Outreach */}
          {outreach && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Generated Outreach</p>

              {/* Channel tabs */}
              <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
                {["email", "instagram_dm", "text", "contact_form"].map((ch) => {
                  const Icon = CHANNEL_ICONS[ch];
                  const label = { email: "Email", instagram_dm: "DM", text: "SMS", contact_form: "Form" }[ch];
                  return (
                    <button
                      key={ch}
                      onClick={() => setActiveChannel(ch)}
                      className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-md text-xs font-medium transition ${
                        activeChannel === ch ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                    </button>
                  );
                })}
              </div>

              {/* Email */}
              {activeChannel === "email" && outreach.email && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-gray-500">Subject</p>
                      <CopyButton text={outreach.email.subject} />
                    </div>
                    <p className="text-sm font-medium text-gray-900">{outreach.email.subject}</p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-medium text-gray-500">Body</p>
                      <CopyButton text={outreach.email.body} />
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{outreach.email.body}</p>
                  </div>
                  {lead.email && (
                    <button
                      onClick={() => handleMarkSent("email")}
                      className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 transition"
                    >
                      <CheckCircle className="w-3 h-3" />
                      Mark as Sent
                    </button>
                  )}
                </div>
              )}

              {/* DM */}
              {activeChannel === "instagram_dm" && outreach.instagram_dm && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500">Instagram DM</p>
                    <CopyButton text={outreach.instagram_dm} />
                  </div>
                  <p className="text-sm text-gray-700">{outreach.instagram_dm}</p>
                  {lead.instagram && (
                    <a
                      href={`https://instagram.com/${lead.instagram.replace("@", "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-3 h-3" /> Open Instagram Profile
                    </a>
                  )}
                </div>
              )}

              {/* SMS */}
              {activeChannel === "text" && outreach.text && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500">SMS</p>
                    <CopyButton text={outreach.text} />
                  </div>
                  <p className="text-sm text-gray-700">{outreach.text}</p>
                </div>
              )}

              {/* Form */}
              {activeChannel === "contact_form" && outreach.contact_form && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-medium text-gray-500">Contact Form Message</p>
                    <CopyButton text={outreach.contact_form} />
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">{outreach.contact_form}</p>
                  {lead.contactForm && (
                    <a
                      href={lead.contactForm}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <ExternalLink className="w-3 h-3" /> Open Contact Form
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {lead.notes && (
            <div>
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Notes</p>
              <p className="text-sm text-gray-600 bg-gray-50 rounded-xl p-4">{lead.notes}</p>
            </div>
          )}

          {/* Danger zone */}
          <div className="pt-2 border-t border-gray-100">
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="flex items-center gap-2 text-xs text-red-400 hover:text-red-600 transition"
              >
                <Trash2 className="w-3 h-3" />
                Delete Lead
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span className="text-xs text-red-500">Are you sure?</span>
                <button onClick={handleDelete} className="text-xs text-red-600 font-medium hover:underline">Yes, delete</button>
                <button onClick={() => setConfirmDelete(false)} className="text-xs text-gray-500 hover:underline">Cancel</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
